import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid2 from '@mui/material/Grid2'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import React from 'react'
import { v1 as uuid } from 'uuid'

import { ContentFlow, ContentFlowElement, ContentFlowPage } from '@/app/module/campaigns/types/common'

import { useWaFlowFieldError } from './hooks/use-wa-flow-field-error'
import WaFlowEditorElements from './wa-flow-editor-elements'
import WaFlowEditorPages from './wa-flow-editor-pages'
import {
  createFooterElement,
  FOOTER_LABEL_MAX_LENGTH,
  mergePageWithFooter,
  updateElementFooter,
} from './wa-flow-editor-utils'

const MIN_PAGE_COUNT = 1
const MAX_PAGE_COUNT = 10
type Props = {
  flow: ContentFlow
  headingLabel?: string
  isNewForm?: boolean
  pageLabel?: string
  pageMissingHelperText?: string
  selectedPageId?: string
  title?: string

  onChange: (flow: ContentFlow) => void
  onPageSelect: (pageId: string) => void
}

const WaFlowEditor: React.FC<Props> = ({
  flow,
  headingLabel = 'Heading',
  isNewForm = false,
  pageLabel = 'Pages',
  pageMissingHelperText = 'At least one page is required to configure this flow.',
  selectedPageId,
  title = 'Edit page',
  onChange,
  onPageSelect,
}) => {
  const canAddPage = flow.pages.length < MAX_PAGE_COUNT
  const canRemovePage = flow.pages.length > MIN_PAGE_COUNT
  const [newPageId, setNewPageId] = React.useState('')
  const [pageVisit, setPageVisit] = React.useState(0)
  const handlePageSelect = React.useCallback(
    (pageId: string) => {
      setNewPageId('')
      setPageVisit((visit) => visit + 1)
      onPageSelect(pageId)
    },
    [onPageSelect],
  )
  const [shouldFocusFooterInput, setShouldFocusFooterInput] = React.useState(false)

  const currentPage = React.useMemo(() => {
    if (!selectedPageId) {
      return flow.pages[0]
    }

    return flow.pages.find((page) => page.id === selectedPageId) || flow.pages[0]
  }, [flow.pages, selectedPageId])

  const currentPageFooterElement = React.useMemo<ContentFlowElement | undefined>(() => {
    if (!currentPage) {
      return undefined
    }

    return currentPage.elements.find((element) => Boolean(element.footer))
  }, [currentPage])

  const currentPageWithoutFooter = React.useMemo<ContentFlowPage | undefined>(() => {
    if (!currentPage) {
      return undefined
    }

    return {
      ...currentPage,
      elements: currentPage.elements.filter((element) => !element.footer),
    }
  }, [currentPage])

  React.useEffect(() => {
    if (selectedPageId || !flow.pages.length) {
      return
    }

    // select initial page if page is not selected yet
    onPageSelect(flow.pages[0].id)
  }, [flow.pages, selectedPageId, onPageSelect])

  React.useEffect(() => {
    if (!currentPageFooterElement || !shouldFocusFooterInput) {
      return
    }

    setShouldFocusFooterInput(false)
  }, [currentPageFooterElement, shouldFocusFooterInput])

  const handlePageChange = React.useCallback(
    (updatedPage: ContentFlowPage) => {
      const pageIndex = flow.pages.findIndex((page) => page.id === updatedPage.id)

      if (pageIndex === -1) {
        return
      }

      const pages = [...flow.pages]
      pages[pageIndex] = updatedPage

      onChange({
        ...flow,
        pages,
      })
    },
    [flow, onChange],
  )

  const handleElementsChange = React.useCallback(
    (updatedPageWithoutFooter: ContentFlowPage) => {
      const mergedPage = mergePageWithFooter(updatedPageWithoutFooter, currentPageFooterElement)

      handlePageChange(mergedPage)
    },
    [currentPageFooterElement, handlePageChange],
  )

  const handleFooterAdd = React.useCallback(() => {
    if (!currentPage || currentPageFooterElement) {
      return
    }

    handlePageChange({
      ...currentPage,
      elements: [...currentPage.elements, createFooterElement(uuid())],
    })
  }, [currentPage, currentPageFooterElement, handlePageChange])

  const handleFooterInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!currentPage || !currentPageFooterElement?.questionId) {
        return
      }

      const updatedPage = updateElementFooter(currentPage, currentPageFooterElement.questionId, event.target.value)
      handlePageChange(updatedPage)
    },
    [currentPage, currentPageFooterElement, handlePageChange],
  )

  const handleFooterRemove = React.useCallback(() => {
    if (!currentPage || !currentPageFooterElement) {
      return
    }

    handlePageChange({
      ...currentPage,
      elements: currentPage.elements.filter((element) => !element.footer),
    })
  }, [currentPage, currentPageFooterElement, handlePageChange])

  const handleFooterToggleChange = React.useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        setShouldFocusFooterInput(true)
        handleFooterAdd()

        return
      }

      setShouldFocusFooterInput(false)
      handleFooterRemove()
    },
    [handleFooterAdd, handleFooterRemove],
  )

  const handlePageAdd = React.useCallback(() => {
    if (!canAddPage) {
      return
    }
    const pageId = uuid()
    const pages = [
      ...flow.pages,
      {
        elements: [],
        id: pageId,
        title: '',
      },
    ]

    onChange({
      ...flow,
      pages,
    })

    setNewPageId(pageId)
    onPageSelect(pageId)
  }, [canAddPage, flow, onChange, onPageSelect])

  const handlePageTitleFocus = React.useCallback(() => {
    setNewPageId('')
  }, [])

  const handlePageRemove = React.useCallback(
    (pageId: string) => {
      if (!canRemovePage) {
        return
      }

      const pageIndex = flow.pages.findIndex((page) => page.id === pageId)

      if (pageIndex === -1) {
        return
      }

      const pages = flow.pages.filter((page) => page.id !== pageId)

      onChange({
        ...flow,
        pages,
      })

      // if the removed page is currently selected, select the first page
      if (pageId === selectedPageId) {
        onPageSelect(pages[0].id)
      }
    },
    [canRemovePage, flow, selectedPageId, onChange, onPageSelect],
  )

  return (
    <Grid2 container spacing={2}>
      <WaFlowEditorPages
        canAddPage={canAddPage}
        canRemovePage={canRemovePage}
        currentPageId={currentPage?.id}
        flow={flow}
        pageLabel={pageLabel}
        pageMissingHelperText={pageMissingHelperText}
        onPageAdd={handlePageAdd}
        onPageRemove={handlePageRemove}
        onPageSelect={handlePageSelect}
      />

      <ContentColumn size={{ xs: 12, md: 8 }}>
        <Stack key={`${flow.id}-${currentPage?.id}-${pageVisit}`} spacing={2}>
          <WaFlowEditorElements
            currentPage={currentPageWithoutFooter}
            headingLabel={headingLabel}
            pageMissingHelperText={pageMissingHelperText}
            shouldFocusTitle={newPageId === currentPage?.id}
            showErrorsOnMount={!newPageId && !(isNewForm && pageVisit === 0)}
            title={title}
            onChange={handleElementsChange}
            onPageTitleFocus={handlePageTitleFocus}
          />

          <FooterEditorCard data-testid="wa-flow-footer-editor" elevation={0}>
            <Stack spacing={1}>
              <FormControlLabel
                control={<Checkbox checked={Boolean(currentPageFooterElement)} onChange={handleFooterToggleChange} />}
                label="Custom footer button"
              />
              {currentPageFooterElement && (
                <FooterButtonField
                  autoFocus={shouldFocusFooterInput}
                  footerElement={currentPageFooterElement}
                  key={currentPageFooterElement.questionId}
                  showErrorsOnMount={!shouldFocusFooterInput && !(isNewForm && pageVisit === 0)}
                  onChange={handleFooterInputChange}
                />
              )}
            </Stack>
          </FooterEditorCard>
        </Stack>
      </ContentColumn>
    </Grid2>
  )
}

type FooterButtonFieldProps = {
  autoFocus?: boolean
  footerElement: ContentFlowElement
  showErrorsOnMount: boolean

  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const FooterButtonField: React.FC<FooterButtonFieldProps> = ({
  autoFocus,
  footerElement,
  showErrorsOnMount,
  onChange,
}) => {
  const [error, revealError] = useWaFlowFieldError(
    footerElement.footer?.label || '',
    FOOTER_LABEL_MAX_LENGTH,
    showErrorsOnMount,
  )

  return (
    <TextField
      autoFocus={autoFocus}
      error={!!error}
      fullWidth
      helperText={error || undefined}
      label="Button"
      required
      value={footerElement.footer?.label || ''}
      variant="standard"
      onBlur={revealError}
      onChange={onChange}
    />
  )
}

const ContentColumn = styled(Grid2)(({ theme }) => ({
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    paddingLeft: theme.spacing(2),
  },
  '&::before': {
    backgroundColor: theme.palette.divider,
    bottom: 0,
    content: '""',
    display: 'none',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '1px',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
}))

const FooterEditorCard = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  bottom: theme.spacing(1),
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.5),
  position: 'sticky',
  width: '100%',
  zIndex: 1,
}))

export default WaFlowEditor

export type { Props as WaFlowEditorProps }
